import { $convertToMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  LexicalCommand,
} from 'lexical';
import { useEffect, useMemo, useState } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { createTransformers } from '../config/transformers';
import { $insertReplacementMarkers, $removeReplacementMarkers, $setEditorValue } from '../utils';

type SiestaRequestPayload = {
  promptText: string;
};

export const SIESTA_SELECTION_REQUEST_COMMAND: LexicalCommand<SiestaRequestPayload> = createCommand(
  'SIESTA_SELECTION_REQUEST',
);

function useSiestaTransformer() {
  return async (markdown: string): Promise<string> => {
    const delay = 500 + Math.random() * 1000;
    await new Promise(resolve => setTimeout(resolve, delay));

    return markdown
      .replaceAll('foo', 'bar')
      .replaceAll('<replacement>', '')
      .replaceAll('</replacement>', '');
  };
}

export function SiestaInlinePlugin() {
  const [editor] = useLexicalComposerContext();
  const transformText = useSiestaTransformer();
  const transformers = useMemo(() => createTransformers(editor), [editor]);

  const [pendingRequest, setPendingRequest] = useState<{
    markdown: string;
    promptText: string;
  } | null>(null);

  useEffect(() => {
    if (!pendingRequest) return;

    const { markdown, promptText } = pendingRequest;

    transformText(markdown)
      .then(replacementMarkdown => {
        const scrollY = window.scrollY;

        editor.update(
          () => {
            $setEditorValue(replacementMarkdown, transformers);
          },
          { tag: 'siesta-replacement' },
        );

        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });

        setPendingRequest(null);
      })
      .catch(error => {
        notifyAndLogError('Siesta AI Error', 'Failed to transform text. Please try again.', error, {
          prompt: promptText,
          markdown,
        });
        setPendingRequest(null);
      });
  }, [pendingRequest, transformText, editor, transformers]);

  useEffect(() => {
    return editor.registerCommand(
      SIESTA_SELECTION_REQUEST_COMMAND,
      (payload: SiestaRequestPayload) => {
        const { promptText } = payload;

        editor.update(() => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return;
          }

          const markers = $insertReplacementMarkers();
          if (!markers) return;

          const root = $getRoot();
          const markdown = $convertToMarkdownString(transformers, root);

          $removeReplacementMarkers(markers);

          if (!markdown.trim()) {
            return;
          }

          $setSelection(null);

          setPendingRequest({ markdown, promptText });
        });

        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor, transformers]);

  return null;
}
